import { Injectable, inject } from '@angular/core';
import { EntityKind, Visibility } from '../models/org-entity';
import { EntityRole } from '../models/entity-member';
import { Callable, FN_REGION } from './callable';

/**
 * Mutations transverses du cycle de vie d'entité (création, adhésion, équipe) et
 * du retrait à deux paliers — communes aux églises ET associations (le champ
 * `kind` discrimine). Régions : W1 (cycle de vie), W3 (retraits/vérification).
 * Voir README §5–§8.
 */
export interface CreateEntityInput {
  kind: EntityKind;
  name: string;
  city: string;
  country: string;
  visibility: Visibility;
  leadName: string;
  /** Église : dénomination. Association : (non utilisé, voir category). */
  denomination?: string;
  /** Association : catégorie de cause. */
  category?: string;
  kycDocs?: string[];
}

export interface JoinEntityInput {
  kind: EntityKind;
  entityId: string;
  /** Token général de l'entité ; optionnel pour rejoindre une entité publique. */
  inviteToken?: string;
  firstName: string;
  lastName: string;
  role: EntityRole;
  motif?: string | null;
  joinedVia?: 'invite_link' | 'qr' | 'search' | 'manual';
}

@Injectable({ providedIn: 'root' })
export class Entity {
  private callable = inject(Callable);

  /** Crée une entité (kycStatus:'pending') + notif admin. Région W1. */
  async createEntity(data: CreateEntityInput): Promise<{ entityId: string; kind: EntityKind }> {
    return this.callable.call<CreateEntityInput, { entityId: string; kind: EntityKind }>(
      FN_REGION.W1,
      'createEntity',
      data
    );
  }

  /** Adhésion 1 clic (status:'pending', accès immédiat). Région W1. */
  async joinEntity(data: JoinEntityInput): Promise<{ success: boolean; alreadyMember?: boolean }> {
    return this.callable.call<JoinEntityInput, { success: boolean; alreadyMember?: boolean }>(
      FN_REGION.W1,
      'joinEntity',
      data
    );
  }

  /**
   * Quitter une entité (auto-départ). Réservé au membre lui-même : le seul
   * responsable / trésorier doit d'abord transférer son rôle. Région W1.
   */
  async leaveEntity(data: {
    kind: EntityKind;
    entityId: string;
  }): Promise<{ success: boolean; alreadyAbsent?: boolean }> {
    return this.callable.call<typeof data, { success: boolean; alreadyAbsent?: boolean }>(
      FN_REGION.W1,
      'leaveEntity',
      data
    );
  }

  /** Résout un token d'invitation → entité (même privée) + inscrit le membre. Région W1. */
  async resolveJoinToken(
    token: string
  ): Promise<{ kind: EntityKind; entityId: string; name: string } | null> {
    return this.callable.call<
      { token: string },
      { kind: EntityKind; entityId: string; name: string } | null
    >(FN_REGION.W1, 'resolveJoinToken', { token });
  }

  /** Invite un responsable (nom + tél + rôle). Région W1. */
  async inviteResponsible(data: {
    kind: EntityKind;
    entityId: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    role: EntityRole;
  }): Promise<{ success: boolean; inviteUrl: string }> {
    return this.callable.call(FN_REGION.W1, 'inviteEntityResponsible', data);
  }

  // ─── Retrait à deux paliers (E) — région W3 (money path) ────────────────────

  /** Crée une demande de retrait d'entité (palier interne en attente). Région W3. */
  async requestEntityWithdrawal(data: {
    entityKind: 'church' | 'association';
    entityId: string;
    requestedAmountUsd: number;
    paymentMethod: 'cash' | 'mobile_money';
    paymentDestination: string;
    reason: string;
    operator?: string | null;
    posPoint?: string | null;
    linkedExpenseId?: string | null;
  }): Promise<{ withdrawalId: string; requiresAdminValidation: boolean }> {
    return this.callable.call(FN_REGION.W3, 'requestEntityWithdrawal', data);
  }

  /**
   * Palier interne (trésorier/lead). Si montant < seuil ⇒ exécute le payout ;
   * sinon laisse 'pending' pour la file admin (2ᵉ palier).
   */
  async validateWithdrawalInternal(data: {
    entityKind: 'church' | 'association';
    entityId: string;
    withdrawalId: string;
  }): Promise<{ success: boolean; executed: boolean; underCap: boolean }> {
    return this.callable.call(FN_REGION.W3, 'validateWithdrawalInternal', data);
  }

  /**
   * Palier 2 (admin plateforme) — finalise un retrait d'entité ≥ seuil après le
   * palier interne. Réservé admin. Région W3.
   */
  async executeEntityWithdrawal(data: {
    entityKind: 'church' | 'association';
    entityId: string;
    withdrawalId: string;
  }): Promise<{ success: boolean; alreadyExecuted?: boolean }> {
    return this.callable.call(FN_REGION.W3, 'executeEntityWithdrawal', data);
  }
}
